import React, { useState } from 'react';
import { useStore } from '../../store/StoreContext';
import { useAuth } from '../../auth/AuthContext';
import { LEAD_SOURCES } from '../../lib/constants';
import { TimePicker, DatePicker, ChoiceKeys, Switch, Button, Segmented, FormField, FormErrorBanner, MobileScreen, MobileHeader, MobileActionBar, MobileCard, mobileInputCls, inputErrorCls, TripStrip } from '../ui';
import RoutePointsEditor from './RoutePointsEditor';
import PhonesEditor, { normalizePhonesForSave } from './PhonesEditor';
import { phonesOf, formatPhone } from '../../lib/phone';
import { findLikelyDuplicates } from '../../lib/clientDupes';
import { useConfirm } from '../../hooks/useConfirm';
import { useToast } from '../../hooks/useToast';

// ===== WALKTHROUGH CLIENT INTAKE =====
// Slim, mobile-friendly mirror of the desktop ClientIntakeModal.
// Used inside the Estimator app so an estimator can paste in client details
// from Google Calendar (or wherever the lead came from) without leaving the
// walkthrough flow. We intentionally cut the move-details / packing / heavy
// items / photo / scheduling fields here — those get filled in during the
// actual walkthrough capture or later on the desktop side. The desktop
// ClientIntakeModal remains the canonical full intake form.
//
// Required fields match the desktop modal's validate(): name, phone,
// origin_address. Defaults match the desktop modal's `defaults` object so
// downstream consumers don't break on missing keys.
//
// Props:
//   onBack         — called when the user taps back / cancels (no save)
//   onSaved(client)— called with the freshly upserted client (has new id);
//                    parent uses this to route into WalkthroughCapture for
//                    the brand new client.
function WalkthroughClientIntake({ onBack, onSaved }) {
    const { saveClient, saveInternalNoteSet, saveJob, saveMove, crew, clients: allClients } = useStore();
    // Duplicate-create guard (2026-07-02) — "Open existing" routes straight
    // into walkthrough capture for the matched client.
    const [dupePrompt, setDupePrompt] = useState(null);
    const dupeOverrideRef = React.useRef(false);
    // Scroll target for the validation banner (the MobileScreen body scrolls).
    const bodyRef = React.useRef(null);
    const { user } = useAuth();
    const [saving, setSaving] = useState(false);
    // Keeps the save button disabled through the brief post-save toast/route
    // delay so a double-tap can't create a second client.
    const [done, setDone] = useState(false);
    const [errors, setErrors] = useState({});
    const { show } = useToast();

    // ── Walkthrough scheduling (Jaylen 2026-07-02) ─────────────────────
    // Julian added a client he was walking through RIGHT NOW and it sank
    // to the bottom of the list — this app had no way to set the
    // walkthrough date/time. Defaults ON (this is the walkthrough app;
    // walking-in-now is the norm) with today + the current 15-min slot
    // pre-filled; the "Just adding a lead" option turns it off for the
    // out-of-office lead-capture case. Writes the SAME fields the desktop
    // scheduler writes (client.walkthrough_date + a WALKTHROUGH move_jobs
    // row carrying the time), so both surfaces stay in sync.
    const localToday = new Date().toLocaleDateString('en-CA');
    const nowSlot = (() => {
        const d = new Date();
        const m = Math.floor(d.getMinutes() / 15) * 15;
        return `${String(d.getHours()).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    })();
    const [scheduleWt, setScheduleWt] = useState(true);
    const [wtDate, setWtDate] = useState(localToday);
    const [wtTime, setWtTime] = useState(nowSlot);

    // Office toasts (feedback.md 1B). Validation never toasts — the field
    // messages + FormErrorBanner carry it (4B).
    const showToast = (msg, isError = false) => show(msg, isError ? 'error' : 'success');

    // Match the desktop modal's defaults so the saved row has the same shape
    // as a phone-intake client. entry_type = 'walkthrough' so we can later
    // tell which leads were captured in person vs. over the phone.
    const [form, setForm] = useState({
        name: '',
        phone: '',
        phones: [],
        email: '',
        lead_source: 'manual',
        status: 'new_lead',
        entry_type: 'walkthrough',
        origin_address: '',
        destination_address: '',
        additional_stops: '',
        job_type: 'LOCAL',
        packing_needed: 'tbd',
        referral_name: '',
        sms_approved: false,
        notes: '',
    });

    // ── Move dates (2026-09-18, trip strip pick 1B) ─────────────────────
    // A new client has no move row until Save, so the TripStrip edits this
    // local draft (Pack · Load · Unload, each tentative on its own). On
    // save the load / pack fields ride the client insert as move_date /
    // move_time / date_tentative / pack_date / pack_time — the DB seeds the
    // lead move from them — and the keys the client row can't carry
    // (unload, pack tentative) go to that move right after, exactly like
    // the office intake. Replaces the Move date picker + tentative switch.
    const EMPTY_DRAFT = {
        load_date: null, load_time: null, load_tentative: false,
        unload_date: null, unload_window_end: null, unload_time: null, unload_tentative: false,
        pack_date: null, pack_time: null, pack_tentative: false,
    };
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    // TripStrip hands back ONLY the changed keys — merge.
    const patchDraft = (patch) => setDraft(d => ({ ...d, ...patch }));

    const set = (k, v) => {
        setForm(f => ({ ...f, [k]: v }));
        if (errors[k]) setErrors(e => ({ ...e, [k]: false }));
    };

    // Unsaved-changes guard (QA M4 BUG-2 — desktop ClientIntakeModal parity).
    // A field estimator one mis-tap from losing a long intake gets an
    // in-app dialog instead of a silent discard. Dirty = anything typed
    // vs the initial defaults; skipped after a successful save.
    const initialFormRef = React.useRef(JSON.stringify(form));
    const { confirm: confirmDialog, confirmElement } = useConfirm();
    const handleBack = async () => {
        // A date picked on the trip strip is an edit too.
        const isDirty = JSON.stringify(form) !== initialFormRef.current || JSON.stringify(draft) !== JSON.stringify(EMPTY_DRAFT);
        if (isDirty && !done) {
            const nm = form.name?.trim();
            const ok = await confirmDialog({
                title: nm ? `Discard ${nm}'s intake?` : 'Discard this new client?',
                body: `Nothing has been saved — everything typed on this form is lost and no client record is created${nm ? ` for ${nm}` : ''}.`,
                confirmLabel: 'Discard intake',
                cancelLabel: 'Keep editing',
                danger: true,
            });
            if (!ok) return;
        }
        onBack?.();
    };

    // Multi-key setter for components like RoutePointsEditor that need to
    // update origin/destination/additional_stops together — collapses to
    // a single render and clears any error on a touched required field.
    const setBatch = (patch) => {
        setForm(f => ({ ...f, ...patch }));
        const touched = Object.keys(patch);
        if (touched.some(k => errors[k])) {
            setErrors(e => {
                const next = { ...e };
                touched.forEach(k => { if (next[k]) next[k] = false; });
                return next;
            });
        }
    };

    const validate = () => {
        const errs = {};
        if (!form.name?.trim()) errs.name = true;
        if (!normalizePhonesForSave(form.phones).length) errs.phone = true;
        if (!form.origin_address?.trim()) errs.origin_address = true;
        // Same rule as the desktop scheduler: a scheduled walkthrough
        // must carry a time (list ordering + the 30-min reminder both
        // key off it).
        if (scheduleWt && (!wtDate || !wtTime)) errs.wt_time = true;
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) {
            // Scroll the summary banner into view so nothing hides below.
            try { bodyRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }); } catch { /* no-op */ }
            return;
        }
        setSaving(true);
        try {
            const cleaned = { ...form };
            // Trip-strip draft → the client insert (same mapping as the
            // office intake, 2026-09-18). The DB seeds the lead move from
            // these columns. Tentative never persists without a date
            // (OBS-3). The pack day is kept even though this slim intake
            // has no packing field: an estimator who added "+ Pack day"
            // means packing is on the job; packing itself is set in the
            // walkthrough Details.
            cleaned.move_date = draft.load_date || null;
            cleaned.move_time = draft.load_time || '';
            cleaned.date_tentative = !!draft.load_date && !!draft.load_tentative;
            cleaned.pack_date = draft.pack_date || null;
            cleaned.pack_time = draft.pack_date ? (draft.pack_time || '') : '';
            cleaned.walkthrough_date = scheduleWt ? wtDate : null;
            // Multi-phone sync (sql/068): normalized array + legacy phone
            // mirroring the primary.
            cleaned.phones = normalizePhonesForSave(cleaned.phones);
            cleaned.phone = cleaned.phones.find(p => p.is_primary)?.number || '';

            // Duplicate guard — once (Create Anyway sets the override).
            // STRONG → blocking dialog; SOFT (name-only) → non-blocking
            // heads-up after save, mirroring the desktop intake (BUG-6).
            let softDup = null;
            if (!dupeOverrideRef.current) {
                const dupe = findLikelyDuplicates(cleaned, allClients);
                if (dupe?.strength === 'strong') {
                    setDupePrompt(dupe);
                    setSaving(false);
                    return;
                }
                if (dupe?.strength === 'soft') softDup = dupe;
            }
            const saved = await saveClient(cleaned);

            // The fields the client row can't carry go to the lead move the
            // insert just created (unload day / window / time / tentative,
            // pack tentative). Best-effort — the client + move exist; the
            // toast says what to fix in the walkthrough if it fails.
            if (saved?.current_move_id) {
                const extra = {};
                if (draft.unload_date) {
                    extra.unload_date = draft.unload_date;
                    extra.unload_window_end = draft.unload_window_end || null;
                    extra.unload_time = draft.unload_time || null;
                    extra.unload_tentative = !!draft.unload_tentative;
                }
                if (cleaned.pack_date && draft.pack_tentative) extra.pack_tentative = true;
                if (Object.keys(extra).length) {
                    try { await saveMove({ id: saved.current_move_id, ...extra }); }
                    catch (me) {
                        console.warn('lead move unload / pack-tentative save failed (client saved):', me);
                        showToast('Saved, but the unload / pack dates didn\'t stick — set them in the walkthrough', true);
                    }
                }
            }

            // Mirror the desktop scheduler: the time (and assignee) live on
            // a WALKTHROUGH move_jobs row. Assignee = this estimator when
            // their first name matches an owner-role crew member (the
            // brothers), else unassigned.
            if (scheduleWt && saved?.id) {
                try {
                    const firstName = ((user?.email || '').split('@')[0] || '').toLowerCase();
                    const me = (crew || []).find(c => c.role === 'owner' && c.active !== false && (c.name || '').toLowerCase().startsWith(firstName));
                    await saveJob({
                        client_id: saved.id,
                        type: 'WALKTHROUGH',
                        job_label: 'Walkthrough',
                        move_date: wtDate,
                        time: wtTime,
                        from_address: cleaned.origin_address || '',
                        to_address: cleaned.destination_address || '',
                        status: me ? 'assigned' : 'unassigned',
                        leader_id: me?.id || null,
                        crew_ids: me ? [me.id] : [],
                        truck_ids: [],
                        duration_days: 1,
                        packing_service: false,
                    });
                } catch (je) {
                    console.warn('walkthrough job create failed (client saved):', je);
                    showToast('Saved, but not scheduled — set the time in the office', true);
                }
            }

            // Forward the intake note into the client's OFFICE notes so it
            // actually surfaces — the legacy clients.notes field it used to
            // write to is no longer shown anywhere in the office UI. Tagged
            // with the estimator's name, same as walkthrough notes.
            try {
                if ((form.notes || '').trim() && saved?.id && saveInternalNoteSet) {
                    const raw = (user?.email || '').split('@')[0] || 'Estimator';
                    const estimatorName = raw.charAt(0).toUpperCase() + raw.slice(1);
                    await saveInternalNoteSet({ client_id: saved.id, label: `${estimatorName}'s Intake note`, body: form.notes.trim() });
                }
            } catch (fe) { console.warn('intake note → office note forward failed:', fe); }

            // Route by intent (BUG-3): a scheduled walkthrough drops into
            // capture; "Just adding a lead" returns to the list so the
            // estimator isn't forced into a walkthrough they didn't ask for.
            // A soft name-dup shows a heads-up first, then routes.
            setDone(true);
            const proceed = () => { if (scheduleWt) onSaved?.(saved); else onBack?.(); };
            if (softDup) {
                showToast(`Heads up: an existing client is also named ${softDup.match.name}`, false);
                setTimeout(proceed, 1400);
            } else if (scheduleWt) {
                showToast('Client created');
                onSaved?.(saved);
            } else {
                showToast('Lead added');
                setTimeout(proceed, 900);
            }
        } catch (e) {
            console.error('WalkthroughClientIntake save error:', e);
            showToast('Save failed', true);
        } finally {
            setSaving(false);
        }
    };

    const errCls = inputErrorCls;
    const fieldCls = (bad) => `${mobileInputCls} ${bad ? errCls : ''}`;
    const textareaCls = `${mobileInputCls} min-h-[100px] py-3 leading-relaxed`;

    // Summary banner items (feedback.md 4B) — same short wording as the
    // field messages.
    const errorItems = [
        errors.name && 'Full name — required',
        errors.phone && 'Phone number — required',
        errors.origin_address && 'Pickup address — required',
        errors.wt_time && 'Walkthrough — pick a date and time',
    ].filter(Boolean);

    return (
        <MobileScreen
            header={<MobileHeader title="New client" sub="Quick intake — full details in the walkthrough" onBack={handleBack} />}
            action={(
                <MobileActionBar>
                    <Button variant="tertiary" size="lg" className="flex-shrink-0" onClick={handleBack} disabled={saving}>Cancel</Button>
                    <Button size="lg" full onClick={handleSave} loading={saving} disabled={done}>Save client</Button>
                </MobileActionBar>
            )}
        >
            <div ref={bodyRef} className="space-y-3">
                <FormErrorBanner items={errorItems} />

                {/* Contact Info */}
                <MobileCard title="Contact" className="space-y-3">
                    <FormField label="Full name" required error={errors.name ? 'Required' : undefined} className="!mb-0">
                        <input
                            className={fieldCls(errors.name)}
                            value={form.name}
                            onChange={e => set('name', e.target.value)}
                            placeholder="Jane Doe"
                            autoCapitalize="words"
                            autoComplete="name"
                        />
                    </FormField>
                    <FormField label="Phone numbers" required error={errors.phone ? 'Required' : undefined} className="!mb-0">
                        <PhonesEditor
                            value={form.phones}
                            onChange={(rows) => { set('phones', rows); if (errors.phone) setErrors(e => ({ ...e, phone: false })); }}
                            inputCls={mobileInputCls}
                            error={errors.phone}
                            compact
                        />
                    </FormField>
                    <FormField label="Email" className="!mb-0">
                        <input
                            type="email"
                            inputMode="email"
                            className={mobileInputCls}
                            value={form.email}
                            onChange={e => set('email', e.target.value)}
                            placeholder="jane@example.com"
                            autoComplete="email"
                            autoCapitalize="off"
                        />
                    </FormField>
                    {/* Single on/off → Switch (form-controls.md). */}
                    <Switch
                        className="w-full bg-slate-50 p-3 rounded-lg"
                        checked={!!form.sms_approved}
                        onChange={bool => set('sms_approved', bool)}
                        label="SMS updates OK"
                        description="Client agreed to receive text updates"
                    />
                </MobileCard>

                {/* Route — multi-stop editor with origin, optional middle
                    stops, destination, plus per-leg drive times and a trip
                    total. Writes back to origin_address / destination_address /
                    additional_stops so the desktop side reads the exact same
                    shape MoveDetailsEditor produces. */}
                <MobileCard title="Route">
                    <p className="text-[11px] text-slate-400 mb-2">Add stops between FROM and TO if the move has multiple pickups or drop-offs.</p>
                    <RoutePointsEditor form={form} set={set} setBatch={setBatch} errors={errors} />
                    {errors.origin_address && <p className="text-xs font-semibold text-red-600 mt-1">Pickup address required</p>}
                </MobileCard>

                {/* Move Basics */}
                <MobileCard title="Move basics" className="space-y-3">
                    {/* Job type first: the trip strip below shapes itself
                        from it (Long distance shows the Unload tile). */}
                    <FormField label="Job type" className="!mb-0">
                        <Segmented
                            full
                            size="md"
                            ariaLabel="Job type"
                            items={[{ value: 'LOCAL', label: 'Local' }, { value: 'LONG_DISTANCE', label: 'Long distance' }]}
                            value={form.job_type}
                            onChange={v => set('job_type', v)}
                            className="[&>button]:h-11"
                        />
                    </FormField>
                    {/* Move dates — the trip strip (1B) over the local draft;
                        each date tentative on its own (dashed amber tile).
                        Its label row carries "+ Pack day" / "+ Unload date".
                        Booking never waits on a tentative date any more
                        (moves-architecture.md rule 4), so no gate copy here. */}
                    <TripStrip
                        value={{ ...draft, job_type: form.job_type, packing_needed: form.packing_needed }}
                        onChange={patchDraft}
                        size="md"
                        label="Move dates"
                        className="!border-0 !p-0 !rounded-none"
                    />
                </MobileCard>

                {/* Walkthrough scheduling — apparent two-way choice, not a
                    buried checkbox. Default = scheduling (this IS the
                    walkthrough app); "Just adding a lead" covers capturing
                    someone while out of the office. */}
                <MobileCard title="Walkthrough" className="space-y-3">
                    <Segmented
                        full
                        size="md"
                        ariaLabel="Schedule a walkthrough or just add a lead"
                        items={[{ value: 'schedule', label: 'Schedule walkthrough' }, { value: 'lead', label: 'Just a lead' }]}
                        value={scheduleWt ? 'schedule' : 'lead'}
                        onChange={v => {
                            if (v === 'schedule') { setScheduleWt(true); return; }
                            setScheduleWt(false);
                            if (errors.wt_time) setErrors(e => ({ ...e, wt_time: false }));
                        }}
                        className="[&>button]:h-11"
                    />
                    {scheduleWt && (
                        <div className="space-y-3">
                            <FormField label="Date" required error={errors.wt_time && !wtDate ? 'Required' : undefined} className="!mb-0">
                                <DatePicker
                                    value={wtDate}
                                    placeholder="Pick a date…"
                                    onChange={v => { setWtDate(v); if (errors.wt_time) setErrors(er => ({ ...er, wt_time: false })); }}
                                />
                            </FormField>
                            <FormField label="Time" required error={errors.wt_time && !wtTime ? 'Pick a time' : undefined} className="!mb-0">
                                <TimePicker
                                    value={wtTime}
                                    onChange={v => { setWtTime(v); if (errors.wt_time) setErrors(er => ({ ...er, wt_time: false })); }}
                                    ariaLabel="Walkthrough time"
                                />
                            </FormField>
                            <p className="text-[11px] text-slate-400">
                                Pre-filled for a walkthrough starting now — adjust if it's later. Keeps the list ordered and fires the 30-min reminder.
                            </p>
                        </div>
                    )}
                </MobileCard>

                {/* Lead Source */}
                <MobileCard title="Lead source" className="space-y-3">
                    <FormField label="How did they find us?" className="!mb-0">
                        {/* Six static sources → ChoiceKeys (form-controls.md
                            short list); the native dropdown opened upward when
                            the last option was selected. */}
                        <ChoiceKeys
                            columns={3}
                            ariaLabel="Lead source"
                            items={LEAD_SOURCES.map(src => ({ value: src, label: src.charAt(0).toUpperCase() + src.slice(1) }))}
                            value={form.lead_source}
                            onChange={v => set('lead_source', v)}
                            keyClassName="!h-11"
                        />
                    </FormField>
                    {form.lead_source === 'referral' && (
                        <FormField label="Referred by" className="!mb-0">
                            <input
                                className={mobileInputCls}
                                value={form.referral_name}
                                onChange={e => set('referral_name', e.target.value)}
                                placeholder="Name of who referred them"
                                autoCapitalize="words"
                            />
                        </FormField>
                    )}
                </MobileCard>

                {/* General Notes */}
                <MobileCard title="Notes">
                    <p className="text-[12px] text-slate-400 mb-2">Anything from the calendar or call worth capturing now.</p>
                    <textarea
                        className={textareaCls}
                        value={form.notes}
                        onChange={e => set('notes', e.target.value)}
                        placeholder="Pasted from Google Calendar, scheduling preferences, etc."
                    />
                </MobileCard>
                {confirmElement}
            </div>

            {/* Possible-duplicate dialog (STRONG match). "Open existing"
                routes straight into walkthrough capture for that client —
                exactly the walking-in-now case. */}
            {dupePrompt && (() => {
                const cands = (dupePrompt.matches && dupePrompt.matches.length ? dupePrompt.matches : [dupePrompt.match]).slice(0, 6);
                const many = cands.length > 1;
                return (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={() => setDupePrompt(null)}>
                    <div className="bg-white rounded-xl border border-slate-200 w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="px-4 pt-4 pb-3">
                            <h3 className="font-bold text-[15px] text-slate-900">Possible duplicate</h3>
                            <p className="text-[13px] text-slate-600 mt-1.5 leading-relaxed">
                                {many ? (
                                    <>This phone number is already on <span className="font-bold">{cands.length} existing clients</span>. Open one, or create a new record.</>
                                ) : (
                                    <>This looks like <span className="font-bold">{cands[0].name}</span>
                                    {cands[0].phone ? ` · ${formatPhone(cands[0].phone)}` : ''} — {dupePrompt.reason}.</>
                                )}
                            </p>
                        </div>
                        <div className="flex flex-col gap-2 px-4 py-3 bg-slate-50 border-t border-slate-200">
                            {cands.map(c => (
                                <Button key={c.id} variant="secondary" full className="justify-start text-left !h-auto py-2.5" onClick={() => { setDupePrompt(null); onSaved?.(c); }}>
                                    <span className="flex flex-col min-w-0">
                                        <span className="block">Open {c.name} — start walkthrough</span>
                                        {c.origin_address && <span className="block text-[11px] font-normal text-slate-500 truncate">{c.origin_address}</span>}
                                    </span>
                                </Button>
                            ))}
                            <Button full onClick={() => { dupeOverrideRef.current = true; setDupePrompt(null); handleSave(); }}>
                                Create anyway — different person
                            </Button>
                            <Button variant="tertiary" full onClick={() => setDupePrompt(null)}>
                                Keep editing
                            </Button>
                        </div>
                    </div>
                </div>
                );
            })()}
        </MobileScreen>
    );
}

export default WalkthroughClientIntake;
